import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DataStorageService } from "./data-storage.service";
import { ApiHttpService } from "./api-http.service";
import { ApiRequestError } from "../models";

export type InitState = 'idle' | 'running' | 'done' | 'error';

@Injectable({
  providedIn: 'root'
})
export class DataLoadService {
  private inFlight: Promise<void> | null = null;
  private readonly initState$ = new BehaviorSubject<InitState>('idle');
  private lastError: unknown = null;

  public readonly isInitializing$: Observable<boolean> = this.initState$.pipe(
    map(s => s === 'running'),
    distinctUntilChanged()
  );

  public readonly state$: Observable<InitState> = this.initState$.asObservable();

  constructor(private dataStorageService: DataStorageService,
              private apiHttpService: ApiHttpService) {
  }

  get state(): InitState {
    return this.initState$.value;
  }

  /**
   * True when the load failed because CloudInsurance did not answer. The user
   * can try again later; there is nothing to correct in the portal.
   */
  get failedUpstream(): boolean {
    return this.lastError instanceof ApiRequestError && this.lastError.isUpstreamFailure;
  }

  initLoading(): Promise<void> {
    if (this.dataStorageService.loaded) {
      return Promise.resolve();
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.initState$.next('running');
    this.lastError = null;
    this.inFlight = this.runInit()
      .then(() => { this.initState$.next('done'); })
      .catch((err) => { this.lastError = err; this.initState$.next('error'); throw err; })
      .finally(() => { this.inFlight = null; });

    return this.inFlight;
  }

  /**
   * Runs the load again after a failure. `/get-current-user` reads
   * CloudInsurance, so a load that failed once can succeed on the next try.
   */
  async retry(): Promise<void> {
    if (this.state === 'running') {
      return this.inFlight ?? Promise.resolve();
    }

    this.initState$.next('idle');

    try {
      await this.initLoading();
    } catch (err) {
      console.error('Retry of the initial data load failed', err);
    }
  }

  reset(): void {
    this.lastError = null;
    this.initState$.next('idle');
  }

  private async runInit(): Promise<void> {
    const [allData, fnolRequests, chatSummaries] = await Promise.all([
      this.apiHttpService.getPetInsurance(),
      this.apiHttpService.getActiveFnolRequests(),
      this.apiHttpService.getVetChatSummaries()
    ]);
    this.dataStorageService.user = await this.apiHttpService.getCurrentUser();
    this.dataStorageService.getAllData = allData || [];
    this.dataStorageService.fnolRequests = fnolRequests || [];
    this.dataStorageService.chatSummaries = chatSummaries || [];
    this.dataStorageService.selectedData = this.dataStorageService.getAllData[0];
    this.dataStorageService.loaded = true;
  }
}
